import { useState, useEffect } from "react";
import { Modal, Input, Button } from "@gradebook/ui";
import { MagnifyingGlassIcon, UserIcon } from "@heroicons/react/24/outline";
import type { UserRole } from "@/store/authStore";
import { cn } from "@/lib/utils";
import { useImpersonatableUsers } from "@/hooks/useCourseQueries";

interface ImpersonatableUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
}

interface ImpersonationUserPickerProps {
  isOpen: boolean;
  onClose: () => void;
  role: UserRole;
  onSelectUser: (user: ImpersonatableUser) => void;
}

const roleLabels: Record<UserRole, string> = {
  admin: "Admin",
  teacher: "Teacher",
  student: "Student",
  parent: "Parent",
  coordinator: "Coordinator",
};

export function ImpersonationUserPicker({
  isOpen,
  onClose,
  role,
  onSelectUser,
}: ImpersonationUserPickerProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce the search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setDebouncedSearch("");
    }
  }, [isOpen]);

  const { data: users = [], isLoading: loading } = useImpersonatableUsers(
    role,
    debouncedSearch,
    isOpen
  );

  const handleSelectUser = (user: ImpersonatableUser) => {
    onSelectUser(user);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Select ${roleLabels[role]} to impersonate`}
      size="md"
    >
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>

        {/* User list */}
        <div className="max-h-64 overflow-y-auto border rounded-md">
          {loading ? (
            <div className="p-4 text-center text-neutral-500">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-4 text-center text-neutral-500">
              {search ? "No users found" : "No users available"}
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {users.map((user) => (
                <li key={user.id}>
                  <Button
                    variant="ghost"
                    onClick={() => handleSelectUser(user)}
                    className={cn(
                      "h-auto w-full justify-start rounded-none px-4 py-3 text-left font-normal transition-colors hover:bg-neutral-50",
                      "flex items-center gap-3"
                    )}
                  >
                    <div className="flex-shrink-0">
                      <UserIcon className="h-8 w-8 text-neutral-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-neutral-900 truncate">
                        {user.firstName} {user.lastName}
                      </p>
                      {user.email && (
                        <p className="text-sm text-neutral-500 truncate">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
}
